// src/mixins/permissionsMixin.js
import Swal from "sweetalert2";
import { format } from "date-fns";
import { wencesApi } from "@/api/wencesApi";

export default {
  data() {
    return {
      userPermissions: [],
      rolesPermisos: {
        admin: [],
        employee: [
          "StablishmentBLock",
          "sells",
          "qrSettings",
          "menuslist",
          "userlist",
          "inventorylist",
          "stablishmentslist",
        ],
        gym: ["sells", "menuslist", "inventorylist"],
        restaurant: ["qrSettings"],
        teacher: ["sells"],
      },
    };
  },
  methods: {
    isPublicAuthRoute() {
      const path = this.$route?.path || "";
      const publicAuthPaths = ["/login", "/register", "/recoverpassword", "/terms"];
      return publicAuthPaths.some((publicPath) => path.startsWith(publicPath));
    },
    async fetchUserPermissions() {
      const token = localStorage.getItem("token");
      if (!token || this.isPublicAuthRoute()) {
        this.userPermissions = [];
        return;
      }

      try {
        const response = await wencesApi.get("/api/users/myprofile");

        if (!response.data.ok) {
          return;
        }

        const userData = response.data.data;

        // Obtener el ID del rol, ya sea que venga como string o como objeto
        let roleId;
        if (typeof userData.role === "string") {
          roleId = userData.role;
        } else if (userData.role && userData.role._id) {
          roleId = userData.role._id;
        }

        if (!roleId) {
          return;
        }

        // Obtener los permisos del rol
        const roleResponse = await wencesApi.get(`/api/roles/${roleId}`);

        if (
          roleResponse.data.success &&
          roleResponse.data.data &&
          roleResponse.data.data.permissions
        ) {
          this.userPermissions = roleResponse.data.data.permissions;
        } else {
          this.userPermissions = [];
        }
      } catch (error) {
        // Evitar logout forzado en rutas públicas sin sesión.
        if (localStorage.getItem("token") && !this.isPublicAuthRoute()) {
          this.$router.push("/logout");
        }
        this.userPermissions = [];
      }
    },
    hasPermission(permission) {
      // Si el usuario tiene all_access, tiene todos los permisos
      if (this.userPermissions.includes("all_access")) {
        return true;
      }

      // Normalizar el permiso a verificar
      const normalizedPermission = permission.toLowerCase();

      // Extraer el modelo y la acción del permiso
      const [model, action] = normalizedPermission.split(".");

      // Verificar si el usuario tiene el permiso en cualquiera de los formatos
      const hasPermission = this.userPermissions.some((p) => {
        const normalizedUserPermission = p.toLowerCase();

        // Verificar diferentes formatos de permisos
        const formats = [
          normalizedPermission, // model.view
          `${model}_${action}`, // model_view
          `${model} - ${action}`, // model - view
          `${model}_manage`, // model_manage
          `${model}.manage`, // model.manage
        ];

        return formats.includes(normalizedUserPermission);
      });

      return hasPermission;
    },
    /** Activar/desactivar features en Billing → System Features. */
    canManageSystemFeatures() {
      return (
        this.hasPermission("systemfeatures.access") ||
        this.hasPermission("access.system.features")
      );
    },
    /** Activar/desactivar envío automático de emails y modo prueba en Billing. */
    canManageEmailModule() {
      return (
        this.canManageSystemFeatures ||
        this.hasPermission("email.module.manage") ||
        this.hasPermission("email_module.manage")
      );
    },
    /** Gestionar tickets de soporte (asignar, mover, estados, tablero). */
    canManageSupportTickets() {
      const role = localStorage.getItem("role");
      if (role === "admin") {
        return true;
      }
      return (
        this.hasPermission("support.technical") ||
        this.hasPermission("soporte.tecnico")
      );
    },
    /** Listado /users bajo Configuración (sidebar, Mi menú, «añadir faltantes»). */
    canAccessUsersSettings() {
      return (
        this.hasPermission("users.view") ||
        this.hasPermission("users.manage") ||
        this.hasPermission("users.edit")
      );
    },
    isRestaurant() {
      const role = localStorage.getItem("role");
      return role == "restaurant" || role == "customer" || role == "employee";
    },
    isGym() {
      const role = localStorage.getItem("role");
      return role == "gym";
    },
    isTeacher() {
      const role = localStorage.getItem("role");
      return role == "teacher";
    },
    isClientGym() {
      const role = localStorage.getItem("role");
      return role == "client";
    },
    isEmployeeRestaurant() {
      const role = localStorage.getItem("role");
      return role == "employee";
    },
    getLocalDate(utcDate, dateformat) {
      const date = new Date(utcDate);

      return format(date, dateformat) + "hs."; // Formato de fecha y hora según la hora local del navegador
    },
    isDemo(showpopup = true) {
      const subdomain = window.location.hostname.split(".")[0];
      if (subdomain == "gotakeitdemo") {
        if (showpopup) {
          Swal.fire({
            title: "DEMO",
            html:
              this.$tn("demoMessage") +
              '<br><a class="btn mt-4 border border-primary" href="https://app.gotake.it/#/register" target="_blank">' +
              this.$tn("register") +
              "</a> ",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
        return true;
      }
      if (subdomain == "gotakeitgym") {
        if (showpopup) {
          Swal.fire({
            title: "DEMO",
            html:
              this.$tn("demoMessage") +
              '<br><a class="btn mt-4 border border-primary"  href="https://app.gotake.it/#/register/gym" target="_blank">' +
              this.$tn("register") +
              "</a> ",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
        return true;
      }
      return false;
    },
    isDemoResto() {
      const subdomain = window.location.hostname.split(".")[0];
      if (subdomain == "gotakeitdemo") {
        return true;
      }
      return false;
    },
    isDemoGym() {
      const subdomain = window.location.hostname.split(".")[0];
      if (subdomain == "gotakeitgym") {
        return true;
      }
      return false;
    },
  },
  async created() {
    await this.fetchUserPermissions();
  },
};
